import { getCurrentFileInfo } from '@/shared/clientFunctions';
import { isDisplayableMediaType } from '@/shared/constants';
import {
  getDispatchedFilePath,
  getMediaType,
  isArchiveMemberSearchablePath,
  resolveDisplayLookupUrl,
  resolveFileInfoMediaDisplayUrl,
  resolveFullS3Url,
  resolvePreferredMediaDisplayForSource,
} from '@/shared/functions';
import type { FileSummary, MediaSource } from '@/shared/types';
import type { Source } from '@/shared/types';

type CitationWithPreviewUrl = Source & { previewUrl?: string; preview_url?: string };

export function getCitationPreviewUrl(source: Source): string {
  const extended = source as CitationWithPreviewUrl;
  const raw = extended.previewUrl ?? extended.preview_url ?? '';
  return typeof raw === 'string' ? raw.trim() : '';
}

/**
 * Whether a candidate citation primary URL may be written to mediaUrl/mediaSrc.
 * Matches timeline/event media gating: displayable type AND not a virtual
 * archive-member searchable path (those 404 on /api/proxy — use inline JSON).
 */
export function isProxyableCitationPrimaryUrl(
  url: string | undefined | null,
  mediaType: string | undefined | null,
  jsonMediaDisplayEnabled: boolean,
): boolean {
  if (!url?.trim()) return false;
  if (isArchiveMemberSearchablePath(url)) return false;
  return isDisplayableMediaType(mediaType, { jsonMediaDisplay: jsonMediaDisplayEnabled });
}

export function resolveCitationPrimaryFromFieldsOnly(
  source: Source,
  jsonMediaDisplayEnabled = false,
): string | undefined {
  const citationPreviewUrl = getCitationPreviewUrl(source);
  if (citationPreviewUrl) {
    const citationPreviewType = getMediaType(
      { mediaType: 'unknown', mediaUrl: citationPreviewUrl } as MediaSource,
      source.fileInfo ?? null,
    );
    if (
      isProxyableCitationPrimaryUrl(
        citationPreviewUrl,
        citationPreviewType,
        jsonMediaDisplayEnabled,
      )
    ) {
      return citationPreviewUrl;
    }
    // Non-displayable / virtual archive preview: fall through to preferred paths.
  }

  const preferred = resolvePreferredMediaDisplayForSource(source, source.fileInfo ?? null);
  if (!preferred.mediaUrl || preferred.selectedBy === 'none') {
    return undefined;
  }
  if (isArchiveMemberSearchablePath(preferred.mediaUrl)) {
    return undefined;
  }
  if (
    preferred.selectedBy === 'previewFilePath' ||
    preferred.selectedBy === 'dispatchedFilePath' ||
    preferred.selectedBy === 'derivedPreview'
  ) {
    // Dispatched/preview JSON only counts as displayable when the flag is on.
    if (
      preferred.mediaType === 'json' &&
      !isDisplayableMediaType(preferred.mediaType, { jsonMediaDisplay: jsonMediaDisplayEnabled })
    ) {
      return undefined;
    }
    return preferred.mediaUrl;
  }
  if (source.fileInfo) {
    if (
      preferred.mediaType === 'json' &&
      !isDisplayableMediaType(preferred.mediaType, { jsonMediaDisplay: jsonMediaDisplayEnabled })
    ) {
      return undefined;
    }
    return preferred.mediaUrl;
  }
  if (
    preferred.selectedBy === 'sourceFilePath' &&
    isDisplayableMediaType(preferred.mediaType, { jsonMediaDisplay: jsonMediaDisplayEnabled })
  ) {
    return preferred.mediaUrl;
  }
  return undefined;
}

/**
 * Path ``resolveCitationPrimaryDisplay`` would send to provenance when the
 * citation fields alone do not already resolve a display URL.
 *
 * A list of citations must collect these paths and load them with one
 * ``getCurrentFileInfoBatch``, then pass that map in. Do not map this
 * helper into one ``GET /files/file-info`` per citation. A single preview
 * (one source, no list) may still look the path up itself.
 */
export function citationFileInfoLookupPath(source: Source): string {
  return resolveDisplayLookupUrl(
    source,
    source.fileInfo ?? null,
    resolveFullS3Url(source, source.sourceFilePath || source.sourceUrl || ''),
  ).trim();
}

/**
 * Paths in ``sources`` that still need provenance file-info.
 *
 * Pass the result to one ``getCurrentFileInfoBatch``. Do not GET
 * ``/files/file-info`` once per path. Citations that already resolve a display
 * URL from their own fields are omitted.
 */
export function citationPathsForFileInfoBatch(
  sources: readonly Source[],
  jsonMediaDisplayEnabled = false,
): string[] {
  const paths: string[] = [];
  const seen = new Set<string>();
  for (const source of sources) {
    if (resolveCitationPrimaryFromFieldsOnly(source, jsonMediaDisplayEnabled)) continue;
    const path = citationFileInfoLookupPath(source);
    if (!path || seen.has(path)) continue;
    seen.add(path);
    paths.push(path);
  }
  return paths;
}

/**
 * Display URL for one citation.
 *
 * When ``prefetchedFileInfo`` contains ``citationFileInfoLookupPath(source)``,
 * that row is used and this function does not call provenance. List callers
 * must pass that map from one ``getCurrentFileInfoBatch``. Do not drop the
 * map and let the ``getCurrentFileInfo`` branch run once per citation — that
 * GET is only for a single preview that was not part of a list.
 */
export async function resolveCitationPrimaryDisplay(
  source: Source,
  cellId: string | null | undefined,
  accessToken: string | null | undefined,
  jsonMediaDisplayEnabled = false,
  prefetchedFileInfo?: Map<string, FileSummary | null>,
): Promise<{ primaryUrl: string | undefined; fileInfo: FileSummary | null }> {
  const fromCitation = resolveCitationPrimaryFromFieldsOnly(source, jsonMediaDisplayEnabled);
  if (fromCitation) {
    return { primaryUrl: fromCitation, fileInfo: source.fileInfo ?? null };
  }

  // For container citations (UFDR/zip/...) the cited extract lives in the
  // dispatched object url, not ``sourceFilePath`` (which is the parent
  // container). Look up file-info against the dispatched object so we attach
  // the extract's metadata (mime + stream flags) rather than the container's.
  const lookupPath = citationFileInfoLookupPath(source);
  let fileInfo: FileSummary | null = source.fileInfo ?? null;
  if (lookupPath && prefetchedFileInfo?.has(lookupPath)) {
    fileInfo = prefetchedFileInfo.get(lookupPath) ?? null;
  } else if (lookupPath && cellId && accessToken) {
    // Single preview only. A citation list must hit the branch above.
    fileInfo = await getCurrentFileInfo(lookupPath, cellId, accessToken);
  }

  return resolveCitationDisplayFromFileInfo(
    source,
    fileInfo,
    lookupPath,
    jsonMediaDisplayEnabled,
  );
}

/** Display URL once file-info is already known. Does not call provenance. */
export function resolveCitationDisplayFromFileInfo(
  source: Source,
  fileInfo: FileSummary | null,
  lookupPath: string,
  jsonMediaDisplayEnabled = false,
): { primaryUrl: string | undefined; fileInfo: FileSummary | null } {
  if (fileInfo) {
    const fromFileInfo = resolveFileInfoMediaDisplayUrl(fileInfo, lookupPath);
    if (fromFileInfo) {
      const fromFileInfoType = getMediaType(
        { mediaType: 'unknown', mediaUrl: fromFileInfo } as MediaSource,
        fileInfo,
      );
      if (
        isProxyableCitationPrimaryUrl(fromFileInfo, fromFileInfoType, jsonMediaDisplayEnabled)
      ) {
        return { primaryUrl: fromFileInfo, fileInfo };
      }
    }
  }

  const previewFilePath =
    typeof source.previewFilePath === 'string' ? source.previewFilePath.trim() : '';
  if (previewFilePath) {
    const previewType = getMediaType(
      { mediaType: 'unknown', mediaUrl: previewFilePath } as MediaSource,
      fileInfo,
    );
    if (isProxyableCitationPrimaryUrl(previewFilePath, previewType, jsonMediaDisplayEnabled)) {
      return { primaryUrl: previewFilePath, fileInfo };
    }
  }

  const dispatchedFilePath = getDispatchedFilePath(source);
  if (dispatchedFilePath) {
    const dispatchedType = getMediaType(
      { mediaType: 'unknown', mediaUrl: dispatchedFilePath } as MediaSource,
      fileInfo,
    );
    if (
      isProxyableCitationPrimaryUrl(dispatchedFilePath, dispatchedType, jsonMediaDisplayEnabled)
    ) {
      return { primaryUrl: dispatchedFilePath, fileInfo };
    }
  }

  if (lookupPath) {
    const lookupType = getMediaType(
      { mediaType: 'unknown', mediaUrl: lookupPath } as MediaSource,
      fileInfo,
    );
    if (isProxyableCitationPrimaryUrl(lookupPath, lookupType, jsonMediaDisplayEnabled)) {
      return { primaryUrl: lookupPath, fileInfo };
    }
  }

  return { primaryUrl: undefined, fileInfo };
}
